import { types } from "@vilocnv/allsetra-core";
import axiosInstance from "app/axiosInstance";
import {
  ICreateObjectRide,
  IRideBulkUpdateKeys,
  IRideBulkUpdateMode,
  IRideBulkUpdateDriver
} from "./../types/objectRideTypes";

class ObjectsService {
  static getAllObjects = async (accountId: string) => {
    return await axiosInstance.get(`/accounts/${accountId}/objects`);
  };

  static getAllBasicInfoObjects = async (accountId: string) => {
    return await axiosInstance.get(`/accounts/${accountId}/objects/basic-info`);
  };

  static getSkrrsObjects = async (accountId: string) => {
    return await axiosInstance.get(`/accounts/${accountId}/objects/skrrs`);
  };

  static getMinimalSkrrsObjects = async ({
    accountId,
    params
  }: {
    accountId: string;
    params: types.IRecordsAggregationBody;
  }) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/objects/skrrs/search/minimal`,
      params
    );
  };

  static getInitialObjectsForDropdownList = async (accountId: string) => {
    return await axiosInstance.get(`/accounts/${accountId}/objects/count`);
  };

  static getObjectsByQuery = async (
    accountId: string,
    data: types.IRecordsAggregationBody
  ) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/objects/search`,
      data
    );
  };

  /**
   * Server-side CSV export for the filtered objects set.
   * BE owns the file columns; body mirrors overview search filters (no paging).
   */
  static exportObjects = async (
    accountId: string,
    data: Pick<types.IRecordsAggregationBody, "where" | "orderBy">
  ) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/objects/export`,
      data,
      { responseType: "blob" }
    );
  };

  static getMinimalObjectsByQuery = async (
    accountId: string,
    data: types.IRecordsAggregationBody
  ) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/objects/search/minimal`,
      data
    );
  };

  static getObjectsLocations = async (
    accountId: string,
    data: types.IMapFilter
  ) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/objects/locations`,
      data
    );
  };

  static getSpecificObjectById = async (
    accountId: string,
    objectId: string
  ) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/objects/${objectId}`
    );
  };

  static getObjectDataForMap = async (accountId: string, objectId: string) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/objects/${objectId}/map`
    );
  };

  static getObjectAlarmConfig = async (accountId: string, objectId: string) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/objects/${objectId}/alarm-settings`
    );
  };

  static updateObjectAwayMode = async (
    accountId: string,
    objectId: string,
    isEnabled: boolean
  ) => {
    if (isEnabled) {
      return await axiosInstance.post(
        `/accounts/${accountId}/objects/${objectId}/alarm-settings/away-mode`
      );
    } else {
      return await axiosInstance.delete(
        `/accounts/${accountId}/objects/${objectId}/alarm-settings/away-mode`
      );
    }
  };

  static postUpdateObject = async (accountId: string, data: any) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/objects/${data.uniqueId}`,
      data
    );
  };

  static patchObjectMilageCorrection = async ({
    accountId,
    objectId,
    data
  }: {
    accountId: string;
    objectId: string;
    data: {
      newMileage: number;
      correctionDate: string;
    };
  }) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/objects/${objectId}/mileage`,
      data
    );
  };

  static patchObjectOperationalHours = async ({
    accountId,
    objectId,
    data
  }: {
    accountId: string;
    objectId: string;
    data: {
      counter: "RunningHours" | "IgnitionHours" | "PtoHours";
      newValue: number;
    };
  }) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/objects/${objectId}/operational-hours`,
      data
    );
  };

  static getObjectOperationalHoursCorrections = async ({
    accountId,
    objectId
  }: {
    accountId: string;
    objectId: string;
  }) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/objects/${objectId}/operational-hours/corrections`
    );
  };

  //
  // Object Rides
  //
  static getObjectRidesByQuery = async (
    { accountId, objectId }: { accountId: string; objectId: string },
    data: types.IRecordsAggregationBody
  ) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/objects/${objectId}/rides/search`,
      data
    );
  };

  static getObjectLastRide = async ({
    accountId,
    objectId
  }: {
    accountId: string;
    objectId: string;
  }) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/objects/${objectId}/rides/search`,
      {
        page: 1,
        itemsPerPage: 1,
        orderBy: [{ field: "startDate", direction: "desc" }]
      }
    );
  };

  static getObjectSpecificRideInfo = async ({
    accountId,
    objectId,
    rideId
  }: {
    accountId: string;
    objectId: string;
    rideId: string;
  }) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/objects/${objectId}/rides/${rideId}`
    );
  };

  static createObjectRide = async (
    {
      accountId,
      objectId
    }: {
      accountId: string;
      objectId: string;
    },
    data: ICreateObjectRide
  ) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/objects/${objectId}/rides`,
      data
    );
  };

  static updateObjectRide = async (
    {
      accountId,
      objectId,
      rideId
    }: { accountId: string; objectId: string; rideId: string },
    data: any
  ) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/objects/${objectId}/rides/${rideId}`,
      data
    );
  };

  static getObjectRideLocations = async ({
    accountId,
    objectId,
    rideId
  }: {
    accountId: string;
    objectId: string;
    rideId: string;
  }) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/objects/${objectId}/rides/${rideId}/locations`
    );
  };

  static updateRideBulkUploadKeys = async (
    accountId: string,
    data: IRideBulkUpdateKeys
  ) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/rides/bulk/key`,
      data
    );
  };

  static updateRideBulkUploadMode = async (
    accountId: string,
    data: IRideBulkUpdateMode
  ) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/rides/bulk/mode`,
      data
    );
  };

  static updateRideBulkUploadDriver = async (
    accountId: string,
    data: IRideBulkUpdateDriver
  ) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/rides/bulk/driver`,
      data
    );
  };

  // Patch Object Milaege
  static updateAccountMileage = async ({
    accountId,
    objectId,
    data
  }: {
    accountId: string;
    objectId: string;
    data: any;
  }) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/objects/${objectId}/mileage`,
      data
    );
  };

  // Object Features
  static getObjectFeatures = async (accountId: string, objectId: string) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/objects/${objectId}/features`
    );
  };

  static getObjectMaintenanceAlertRules = async (
    accountId: string,
    objectId: string
  ) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/objects/${objectId}/maintenance-alert-rules`
    );
  };

  static getAccountMaintenanceAlertRules = async (accountId: string) => {
    return await axiosInstance.get(
      `/accounts/${accountId}/maintenance-alert-rules`
    );
  };

  static postMaintenanceAlertRule = async (
    accountId: string,
    data: {
      name: string;
      description: string;
      metric: number;
      firstTrigger: number;
      interval: number;
      recipients: string[];
      selectAllObjects: boolean;
      objectIds: string[];
    }
  ) => {
    return await axiosInstance.post(
      `/accounts/${accountId}/maintenance-alert-rules`,
      data
    );
  };

  static patchMaintenanceAlertRule = async (
    accountId: string,
    ruleId: string,
    data: {
      uniqueId: string;
      name: string;
      description: string;
      metric: number;
      firstTrigger: number;
      interval: number;
      recipients: string[];
      selectAllObjects: boolean;
      objectIds: string[];
    }
  ) => {
    return await axiosInstance.patch(
      `/accounts/${accountId}/maintenance-alert-rules/${ruleId}`,
      data
    );
  };

  static deleteMaintenanceAlertRule = async (
    accountId: string,
    ruleId: string
  ) => {
    return await axiosInstance.delete(
      `/accounts/${accountId}/maintenance-alert-rules/${ruleId}`
    );
  };

  static deleteObject = async (accountId: string, objectId: string) => {
    return await axiosInstance.delete(
      `/accounts/${accountId}/objects/${objectId}`
    );
  };
}

export default ObjectsService;
